import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { cancelInvoice } from "api";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";

interface CancelInvoiceDialogProps {
  isOpen: boolean;
  close: () => void;
  currentInvoice: {
    invoice_id: string;
  };
}

export default function CancelInvoiceDialog({ isOpen, close, currentInvoice }: CancelInvoiceDialogProps) {
  const intl = useIntl();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { mutate: cancelInvoiceMutation, isLoading } = useMutation({
    mutationFn: (invoiceId: string) => cancelInvoice(invoiceId),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "billing.cancelUpdateDialog.cancelSuccess",
          defaultMessage: "Invoice cancelled successfully.",
        }),
        { type: "success" },
      );
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.shipmentInvoicesListing(id!) });
      close();
    },
    onError: () =>
      errorToast(
        intl.formatMessage({
          id: "billing.cancelUpdateDialog.cancelFail",
          defaultMessage: "Fail to cancel invoice.",
        }),
      ),
  });

  const handleCancelInvoice = () => {
    if (!currentInvoice?.invoice_id) return;
    cancelInvoiceMutation(currentInvoice.invoice_id);
  };

  return (
    <Dialog
      show={isOpen}
      title={intl.formatMessage({
        id: "billing.cancelUpdateDialog.title",
        defaultMessage: "Cancel & Update Invoice",
      })}
      onHide={close}
      className="template-dialog-wrapper"
    >
      <h5 className="mb-3">
        <FormattedMessage
          id="billing.cancelUpdateDialog.header"
          defaultMessage="Are you sure you want to cancel this invoice?"
        />
      </h5>

      <p className="mb-3">
        <FormattedMessage
          id="billing.cancelUpdateDialog.subheader1"
          defaultMessage="You will be able to re-submit invoice with adjusted cost details and attachments."
        />
      </p>

      <p className="mb-3">
        <FormattedMessage
          id="billing.cancelUpdateDialog.subheader2"
          defaultMessage="Shipper will be notified that invoice was canceled and will be submitted later for approval."
        />
      </p>

      <hr className="mt-4 mb-4" />

      <div className="d-flex justify-content-between">
        <Button variant="danger" onClick={close} disabled={isLoading}>
          <FormattedMessage id="general.no" defaultMessage="No" />
        </Button>
        <Button variant="primary" onClick={handleCancelInvoice} disabled={isLoading}>
          <FormattedMessage id="billing.cancelUpdateDialog.yesCancel" defaultMessage="Yes, cancel" />
        </Button>
      </div>
    </Dialog>
  );
}
